import {
  BD_CITIES,
  BD_CITY_MAP,
  getPreset,
  type BDCity,
  type RoutePreset,
} from "@/data/bdCities";
import {
  convertLength,
  type LengthUnitKey,
} from "@/lib/calculators/feetToMeter";

export const EARTH_RADIUS_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** Great-circle distance (Haversine) in kilometers. */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/** Initial bearing from point 1 to point 2 (0–360°). */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (toDeg(θ) + 360) % 360;
}

export function compassDirection(deg: number): { en: string; bn: string; short: string } {
  const dirs: { en: string; bn: string; short: string }[] = [
    { en: "North",     bn: "উত্তর",         short: "N"  },
    { en: "Northeast", bn: "উত্তর-পূর্ব",   short: "NE" },
    { en: "East",      bn: "পূর্ব",         short: "E"  },
    { en: "Southeast", bn: "দক্ষিণ-পূর্ব",  short: "SE" },
    { en: "South",     bn: "দক্ষিণ",        short: "S"  },
    { en: "Southwest", bn: "দক্ষিণ-পশ্চিম", short: "SW" },
    { en: "West",      bn: "পশ্চিম",        short: "W"  },
    { en: "Northwest", bn: "উত্তর-পশ্চিম",  short: "NW" },
  ];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
}

export interface CityDistance {
  from: BDCity;
  to: BDCity;
  haversineKm: number;
  bearing: number;
  preset?: RoutePreset;
}

export function cityDistance(fromKey: string, toKey: string): CityDistance | null {
  const from = BD_CITY_MAP[fromKey];
  const to = BD_CITY_MAP[toKey];
  if (!from || !to) return null;
  return {
    from,
    to,
    haversineKm: haversineKm(from.lat, from.lon, to.lat, to.lon),
    bearing: bearingDeg(from.lat, from.lon, to.lat, to.lon),
    preset: getPreset(fromKey, toKey),
  };
}

/* ---------- Speed × Time × Distance ---------- */

export type SpeedUnit = "kmh" | "mph" | "ms";

const SPEED_TO_KMH: Record<SpeedUnit, number> = {
  kmh: 1,
  mph: 1.609344,
  ms: 3.6,
};

export function speedToKmh(speed: number, unit: SpeedUnit): number {
  return speed * SPEED_TO_KMH[unit];
}

export function kmhToSpeed(kmh: number, unit: SpeedUnit): number {
  return kmh / SPEED_TO_KMH[unit];
}

/** speed × time → distance (km). Time in hours. */
export function distanceFromSpeedTime(speed: number, speedUnit: SpeedUnit, hours: number): number {
  return speedToKmh(speed, speedUnit) * hours;
}

/** distance (km) ÷ speed → hours. */
export function timeFromDistanceSpeed(km: number, speed: number, speedUnit: SpeedUnit): number {
  const kmh = speedToKmh(speed, speedUnit);
  return kmh > 0 ? km / kmh : Infinity;
}

/** distance (km) ÷ time (hours) → speed in chosen unit. */
export function speedFromDistanceTime(km: number, hours: number, speedUnit: SpeedUnit): number {
  if (hours <= 0) return Infinity;
  const kmh = km / hours;
  return kmhToSpeed(kmh, speedUnit);
}

export function hoursToHm(hours: number): { h: number; m: number } {
  if (!Number.isFinite(hours)) return { h: 0, m: 0 };
  const total = Math.max(0, Math.round(hours * 60));
  return { h: Math.floor(total / 60), m: total % 60 };
}

/* ---------- Length unit converter (delegates to feetToMeter) ---------- */

export function convertDistance(value: number, from: LengthUnitKey, to: LengthUnitKey): number {
  return convertLength(value, from, to);
}

export { BD_CITIES, BD_CITY_MAP, getPreset };
export type { BDCity, RoutePreset };
